import React, { FC, useState } from 'react';
import { Modal } from 'antd';
import { APP_LINKS } from 'src/config/app-links.config';
import { ReactComponent as CloseIcon } from 'src/assets/images/onboarding-trainer/dashboard/programs/close-icon.svg';
import { ReactComponent as CopyLink } from 'src/assets/images/onboarding-trainer/dashboard/programs/copy_link.svg';
import { ReactComponent as MiniPTApp } from 'src/assets/images/onboarding-trainer/dashboard/programs/minipt-app.svg';
import { ReactComponent as Messenger } from 'src/assets/images/onboarding-trainer/dashboard/programs/messenger.svg';
import { ReactComponent as SMS } from 'src/assets/images/onboarding-trainer/dashboard/programs/sms.svg';

interface ShareProgramModalProps {
  isOpen: boolean;
  onClose: () => void;
  programId: string;
  programmeName: string;
  isSent?: boolean;
  isSending?: boolean;
  onSendMiniPTApp: () => void;
}

export const ShareProgramModal: FC<ShareProgramModalProps> = ({
  isOpen,
  onClose,
  programId,
  programmeName,
  isSent = false,
  isSending = false,
  onSendMiniPTApp,
}) => {
  const appStoreLink = APP_LINKS.ios;
  const shareLink = `${window.location.origin}/program/${programId}`;
  const shareText = `Your PT has sent you “${programmeName}”. (click on program name to open)\nView it in the miniPT app. If you don’t have miniPT yet, install the app first, then open this link:\n${shareLink}\n\nDownload on the App Store:\n${appStoreLink}`;
  const encodedShareText = encodeURIComponent(shareText);

  const [isCopied, setIsCopied] = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareText);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodedShareText}`, '_blank');
  };

  const handleMessenger = () => {
    window.open(`fb-messenger://share/?link=${encodeURIComponent(shareLink)}`, '_blank');
  };

  const handleSMS = () => {
    window.open(`sms:?body=${encodedShareText}`, '_self');
  };

  return (
    <Modal
      open={isOpen}
      footer={null}
      onCancel={onClose}
      centered
      width={500}
      closeIcon={null}
      rootClassName="share-program-modal"
      className="share-program-modal"
      styles={{
        mask: {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(6, 7, 35, 0.8)',
        },
      }}
    >
      <div className="share-modal-content">
        <div className="modal-header">
          <h3>Share Program</h3>
          <button className="close-btn" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <p className="modal-subtitle">Share &quot;{programmeName}&quot; with your client</p>

        <div className="main-options">
          <button
            className={`share-option-btn primary-option ${isCopied ? 'copied-btn' : ''}`}
            onClick={handleCopyLink}
          >
            <div className="icon-box copy-link">
              {isCopied ? (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="check-icon"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                <CopyLink />
              )}
            </div>
            <div className="option-text">
              <span className="option-title">{isCopied ? 'Link Copied!' : 'Copy Link'}</span>
              <span className="option-desc">
                {isCopied ? 'Copied to clipboard!' : 'Copy shareable link to clipboard'}
              </span>
            </div>
          </button>

          <button
            className="share-option-btn primary-option"
            onClick={onSendMiniPTApp}
            disabled={isSending}
          >
            <div className="icon-box minipt-app">
              <MiniPTApp />
            </div>
            <div className="option-text">
              <span className="option-title">
                {isSending ? 'Sending...' : isSent ? 'Send Again' : 'Send via miniPT App'}
              </span>
              <span className="option-desc">
                {isSent
                  ? "Resend and update programme in client's miniPT app"
                  : "Send directly to client's miniPT app"}
              </span>
            </div>
          </button>
        </div>

        <div className="modal-divider">
          <span>Or share via</span>
        </div>

        <div className="secondary-options">
          <button className="share-option-btn secondary-option" onClick={handleWhatsApp}>
            <div className="icon-box social-icon">
              <Messenger />
            </div>
            <span>WhatsApp</span>
          </button>

          <button className="share-option-btn secondary-option" onClick={handleMessenger}>
            <div className="icon-box social-icon">
              <Messenger />
            </div>
            <span>Messenger</span>
          </button>

          <button className="share-option-btn secondary-option" onClick={handleSMS}>
            <div className="icon-box social-icon">
              <SMS />
            </div>
            <span>SMS</span>
          </button>
        </div>
      </div>
    </Modal>
  );
};
